import { useState, useEffect } from "react";
import { invoke } from "~/runtime";
import type { OrderForm, OrderFormItem } from "@decocms/apps-vtex/types";

export type { OrderForm, OrderFormItem };

let _orderForm: OrderForm | null = null;
let _loading = false;
let _initStarted = false;
let _initFailed = false;
const _listeners = new Set<() => void>();

function notify() {
  _listeners.forEach((fn) => fn());
}
function setOrderForm(of: OrderForm | null) {
  _orderForm = of;
  notify();
}
function setLoading(v: boolean) {
  _loading = v;
  notify();
}

function getOrderFormIdFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/checkout\.vtex\.com__orderFormId=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function setOrderFormIdCookie(id: string) {
  if (typeof document === "undefined") return;
  document.cookie = `checkout.vtex.com__orderFormId=${encodeURIComponent(id)}; path=/; max-age=${7 * 24 * 3600}; SameSite=Lax`;
}

async function ensureOrderForm(): Promise<string> {
  if (_orderForm?.orderFormId) return _orderForm.orderFormId;

  const existing = getOrderFormIdFromCookie();
  const of = await invoke.vtex.actions.getOrCreateCart({
    data: { orderFormId: existing || undefined },
  });
  setOrderForm(of);
  if (of?.orderFormId) setOrderFormIdCookie(of.orderFormId);
  return of.orderFormId;
}

export function itemToAnalyticsItem(item: OrderFormItem & { coupon?: string }, index: number) {
  return {
    item_id: item.productId,
    item_group_id: item.productId,
    item_name: item.name ?? item.skuName ?? "",
    item_variant: item.skuName,
    item_brand: item.additionalInfo?.brandName ?? "",
    price: (item.sellingPrice ?? item.price ?? 0) / 100,
    discount: Number(((item.listPrice - item.sellingPrice) / 100).toFixed(2)),
    quantity: item.quantity,
    coupon: item.coupon,
    affiliation: item.seller,
    index,
  };
}

export function resetCart() {
  _orderForm = null;
  _loading = false;
  _initStarted = false;
  _initFailed = false;
  notify();
}

// ---------------------------------------------------------------------------
// Module-level stable refs. Previously useCart() rebuilt these on every call
// — every consumer saw fresh identities for `cart`, `loading`, `updateItems`,
// etc. on every render. Cart.tsx:44-59 used `updateItems` inside a useEffect
// deps array, so the new identity on each render re-triggered the effect,
// which set loading, which notified listeners, which forced re-renders,
// which produced new identities — a loop that fried the main thread during
// the Cart hydration on every page load. Moving the API out of the hook
// body restores stable identity across all renders.
// ---------------------------------------------------------------------------

const stableCart = {
  get value() { return _orderForm; },
  set value(v: OrderForm | null) { setOrderForm(v); },
};

const stableLoading = {
  get value() { return _loading; },
  set value(v: boolean) { setLoading(v); },
};

const stableInitFailed = {
  get value() { return _initFailed; },
};

async function _addItem(params: { id: string; seller: string; quantity?: number }) {
  setLoading(true);
  try {
    const ofId = await ensureOrderForm();
    const updated = await invoke.vtex.actions.addItemsToCart({
      data: {
        orderFormId: ofId,
        orderItems: [{ id: params.id, seller: params.seller, quantity: params.quantity ?? 1 }],
      },
    });
    setOrderForm(updated);
    if (updated?.orderFormId) setOrderFormIdCookie(updated.orderFormId);
  } catch (err) {
    console.error("[useCart] addItem failed:", err);
    throw err;
  } finally {
    setLoading(false);
  }
}

async function _addItems(params: {
  orderItems: Array<{ id: string; seller: string; quantity: number }>;
}) {
  setLoading(true);
  try {
    const ofId = await ensureOrderForm();
    const updated = await invoke.vtex.actions.addItemsToCart({
      data: { orderFormId: ofId, orderItems: params.orderItems },
    });
    setOrderForm(updated);
    if (updated?.orderFormId) setOrderFormIdCookie(updated.orderFormId);
  } catch (err) {
    console.error("[useCart] addItems failed:", err);
    throw err;
  } finally {
    setLoading(false);
  }
}

async function _updateItems(params: { orderItems: Array<{ index: number; quantity: number }> }) {
  const ofId = _orderForm?.orderFormId || getOrderFormIdFromCookie();
  if (!ofId) return;
  setLoading(true);
  try {
    const updated = await invoke.vtex.actions.updateCartItems({
      data: { orderFormId: ofId, orderItems: params.orderItems },
    });
    setOrderForm(updated);
  } catch (err) {
    console.error("[useCart] updateItems failed:", err);
  } finally {
    setLoading(false);
  }
}

async function _removeItem(index: number) {
  const ofId = _orderForm?.orderFormId || getOrderFormIdFromCookie();
  if (!ofId) return;
  setLoading(true);
  try {
    const updated = await invoke.vtex.actions.updateCartItems({
      data: { orderFormId: ofId, orderItems: [{ index, quantity: 0 }] },
    });
    setOrderForm(updated);
  } catch (err) {
    console.error("[useCart] removeItem failed:", err);
  } finally {
    setLoading(false);
  }
}

async function _addCouponsToCart({ text }: { text: string }) {
  const ofId = _orderForm?.orderFormId || getOrderFormIdFromCookie();
  if (!ofId) return;
  setLoading(true);
  try {
    const updated = await invoke.vtex.actions.addCouponToCart({
      data: { orderFormId: ofId, text },
    });
    setOrderForm(updated);
  } catch (err) {
    console.error("[useCart] addCoupon failed:", err);
  } finally {
    setLoading(false);
  }
}

async function _sendAttachment(params: { attachment: string; body: Record<string, unknown> }) {
  const ofId = _orderForm?.orderFormId || getOrderFormIdFromCookie();
  if (!ofId) return;
  setLoading(true);
  try {
    const updated = await invoke.vtex.actions.updateOrderFormAttachment({
      data: {
        orderFormId: ofId,
        attachment: params.attachment,
        body: params.body,
      },
    });
    setOrderForm(updated);
  } catch (err) {
    console.error("[useCart] sendAttachment failed:", err);
  } finally {
    setLoading(false);
  }
}

async function _simulate(data: {
  items: Array<{ id: string; quantity: number; seller: string }>;
  postalCode: string;
  country: string;
}) {
  return await invoke.vtex.actions.simulateCart({
    data: {
      items: data.items.map((i) => ({
        id: i.id,
        quantity: i.quantity,
        seller: i.seller,
      })),
      postalCode: data.postalCode,
      country: data.country,
    },
  });
}

function _mapItemsToAnalyticsItems(orderForm: OrderForm | null) {
  return (orderForm?.items || []).map((item, index) => itemToAnalyticsItem(item, index));
}

const stableCartApi = {
  cart: stableCart,
  loading: stableLoading,
  initFailed: stableInitFailed,
  addItem: _addItem,
  addItems: _addItems,
  updateItems: _updateItems,
  removeItem: _removeItem,
  addCouponsToCart: _addCouponsToCart,
  sendAttachment: _sendAttachment,
  simulate: _simulate,
  mapItemsToAnalyticsItems: _mapItemsToAnalyticsItems,
} as const;

// Non-reactive accessor. Returns the same stable API surface as useCart()
// but does NOT register a listener — use this in components that only
// read cart inside event handlers or one-shot effects (e.g. AddToCartButton's
// click handler) and rely on a parent's useCart() to drive re-renders.
export function getCart() {
  return stableCartApi;
}

/**
 * Reactive cart *value*. Prefer this over `useCart().cart.value` in render
 * bodies: `cart` is a module-level object with a stable identity, so React
 * Compiler caches the `.value` property read against that identity and the
 * component keeps rendering the first snapshot it ever saw (same class of bug
 * as DECO-5817). Returning the OrderForm straight out of `useState` makes it a
 * real reactive dependency the compiler tracks.
 */
export function useCartValue(): OrderForm | null {
  // Delegate init (getOrCreateCart on first mount) to useCart.
  useCart();

  const [snapshot, setSnapshot] = useState<OrderForm | null>(_orderForm);

  useEffect(() => {
    const listener = () => setSnapshot(_orderForm);
    _listeners.add(listener);
    // Catch anything that landed between the initial render and this effect.
    listener();
    return () => {
      _listeners.delete(listener);
    };
  }, []);

  return snapshot;
}

export function useCart() {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const listener = () => forceRender((n) => n + 1);
    _listeners.add(listener);

    if (!_orderForm && !_initStarted) {
      _initStarted = true;
      const ofId = getOrderFormIdFromCookie();
      setLoading(true);
      invoke.vtex.actions
        .getOrCreateCart({ data: { orderFormId: ofId || undefined } })
        .then((of: OrderForm) => {
          setOrderForm(of);
          if (of?.orderFormId) setOrderFormIdCookie(of.orderFormId);
        })
        .catch((err: unknown) => {
          console.error("[useCart] init failed:", err);
          if (!_orderForm) {
            _initFailed = true;
            notify();
          }
        })
        .finally(() => setLoading(false));
    }

    return () => {
      _listeners.delete(listener);
    };
  }, []);

  return stableCartApi;
}
